import React from 'react';
import {defineMessages} from 'react-intl';
import {defineAccessControlledResource, GroupAccessRule} from '@igp/shared/access-control';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import {ErrorModal} from '@igp/shared/components/error';
import {LayoutQueue, PageHeader, Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import {withTraceErrorBoundary} from '@igp/shared/otel';
import {RealtimeMessageTrigger, RealtimeUpdatesMode} from '@igp/shared/redux/realtime';
import type {ViewType} from '@igp/shared/redux/view';
import {extendedViewCleanDelay} from '@igp/shared/redux/view';

import {groups, useBoAgentAccessCheck, withAccessCheck} from '@access-control';
import type {BoUserDataGridColumns, BoUserViewModelKeys} from '@features/bo-user';
import {AddUserButton, BoUserDataGrid, BoUserQueryFilters, useBoUser} from '@features/bo-user';
import {BoUserDataGridColumnsTypeName, BoUserViewModelKeysTypeName} from '@features/bo-user/constants';

const localized = defineMessages({
    title: {
        id: 'BoUsers_title',
        defaultMessage: 'User Management',
    },
});

function BoUsersInner() {
    const mapper = useAutoMapper();
    const canEdit = useBoAgentAccessCheck(boUsersEditResource);
    const viewType: ViewType = 'Users';
    const boUserColumns: BoUserDataGridColumns[] = [
        'uuid',
        'username',
        'firstName',
        'lastName',
        'email',
        'role',
        'aal',
        'agentPlayerId',
        ...(canEdit ? (['edit', 'resetPassword'] satisfies BoUserDataGridColumns[]) : []),
        'accountStatus',
        ...(canEdit ? (['unlinkAgent'] satisfies BoUserDataGridColumns[]) : []),
    ];
    const boUserFields = mapper.mapArray<BoUserDataGridColumns, BoUserViewModelKeys>(
        boUserColumns,
        BoUserDataGridColumnsTypeName,
        BoUserViewModelKeysTypeName
    );
    const {items, rawItems, totalCount, handleFilterChange, handlePageChange, handlePageSizeChange, filterString, searchFilter} = useBoUser(
        {
            viewType,
            fields: boUserFields,
            validateFilter: () => true,
            syncWithUrl: true,
            defaultPaging: {page: 1, pageSize: 25},
            cleanDelay: extendedViewCleanDelay,
            realtimeMode: RealtimeUpdatesMode.Silent,
            triggers: [
                {
                    type: RealtimeMessageTrigger.Update,
                },
            ],
        }
    );
    return (
        <LayoutQueue
            header={<PageHeader header={localized.title} />}
            toolbar={
                <Toolbar>
                    <ToolbarItemsContainer primary>
                        <ToolbarItems primary>
                            <BoUserQueryFilters
                                model={filterString}
                                onChange={handleFilterChange}
                                availableFilters={['all-text-filters', 'role', 'aal']}
                            />
                        </ToolbarItems>
                        <ToolbarItems secondary>
                            <AddUserButton />
                        </ToolbarItems>
                    </ToolbarItemsContainer>
                </Toolbar>
            }
            body={
                <BoUserDataGrid
                    rows={items}
                    rawModels={rawItems}
                    paging={{
                        page: searchFilter?.paging?.page,
                        pageSize: searchFilter?.paging?.pageSize,
                        rowCount: totalCount,
                    }}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    columnKeys={boUserColumns}
                />
            }
        />
    );
}

export const boUsersEditResource = defineAccessControlledResource({
    name: 'BoUsersEdit',
    rules: new GroupAccessRule([groups.adminsGroup]),
});

export const boUsersPageResource = defineAccessControlledResource({
    name: 'BoUsersView',
    rules: new GroupAccessRule([groups.adminsGroup, groups.agentManagersGroup]),
});

const BoUsersWithAccess = withAccessCheck(BoUsersInner, boUsersPageResource);

export const BoUsers = withTraceErrorBoundary(BoUsersWithAccess, ErrorModal);
